import React, { useCallback, useEffect, useRef, useState } from 'react';

import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import { report, reportExport } from 'api/endpoints/report';
import Button from 'components/Button';
import { ENUM_TYPE_DOWNLOAD } from 'constants/enum';
import { TEXT_COMMON, TEXT_DOWNLOAD } from 'constants/string/texts';
import { toastError } from 'lib/toast';
import Table from 'pages/Report/Download/Table';
import { setOpenDownload } from 'store/actions/report';
import { OPTION_PAGINATION } from 'constants/index';
import { convertToQueryString } from 'utils/helper';
import { setLoading } from 'store/actions/common';
import ReportPagination from '../List/ReportPagination';
import _ from 'lodash';

const Download = () => {
  const dispatch = useDispatch();

  const { openDownload } = useSelector((state) => state.report);
  const { me } = useSelector((state) => state.auth);

  const [options, setOptions] = useState({ keyword: null, from_date: null, to_date: null, ...OPTION_PAGINATION });
  const [selectedRows, setSelectedRows] = useState([{ page: 1, selected: [] }]);
  const [sort, setSort] = useState([]);
  const [listSurveys, setListSurveys] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const modalRef = useRef(null);

  // PAGINATION
  const onPageChange = (e) => {
    if (e !== options.page) {
      setOptions((prevState) => {
        return { ...prevState, page: e };
      });
      return true;
    }
    return false;
  };

  // SORT
  const onSortChange = (e) => {
    setSort(() => e);
  };

  const handleSort = () => {
    return (
      sort.map((item) => {
        return item.desc ? `-${item.id}` : item.id;
      }) || []
    );
  };

  const handleGetSurveys = async () => {
    const sortHandle = handleSort();
    const queryString = convertToQueryString({
      keyword: options.keyword,
      from_date: options.from_date,
      to_date: options.to_date,
      sort_by: sortHandle,
      page: options.page,
    });
    setLoadingData(true);
    try {
      await report(queryString)
        .then((response) => {
          const totalPage = Math.ceil(response.total / response.page_size);
          const page = response.page > totalPage ? (totalPage === 0 ? 1 : totalPage) : options.page;

          setListSurveys(response?.data || []);
          setOptions((prevState) => {
            return {
              ...prevState,
              total: response.total,
              page_size: response.page_size,
              page: page,
            };
          });
          setLoadingData(false);
        })
        .catch(() => {
          setLoadingData(false);
        });
    } catch {
      setLoadingData(false);
    }
  };

  // GET DATA
  const getSurveys = useCallback(async () => {
    if (me) {
      handleGetSurveys();
    }
  }, [options.page, options.keyword, sort, me]);

  useEffect(() => {
    getSurveys();
  }, [getSurveys]);

  // HANDLE MODAL
  const closeModal = () => {
    dispatch(setOpenDownload(false));
    setOptions({ keyword: null, ...OPTION_PAGINATION });
    setSelectedRows([{ page: 1, selected: [] }]);
    setSort([]);
    setListSurveys([]);
    setLoadingData(false);
  };

  const onConfirm = async () => {
    const listSurveysDelete = _.flatMap(selectedRows, 'selected');

    dispatch(setLoading({ isOpen: true }));
    await reportExport({ report_ids: listSurveysDelete }, openDownload.type, me.lang)
      .then((res) => {
        dispatch(setLoading({ isOpen: false }));
      })
      .catch((error) => {
        toastError(error.message);
        dispatch(setLoading({ isOpen: false }));
      });
    closeModal();
  };

  useEffect(() => {
    if (openDownload.isOpen) {
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('h-screen');
    } else {
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('h-screen');
    }
  }, [openDownload]);

  const clickBackdrop = () => {
    modalRef.current.classList.add('animate-fade-out');
    setTimeout(() => {
      modalRef.current.classList.remove('animate-fade-out');
    }, 300);
  };

  if (!openDownload.isOpen) {
    return null;
  }

  const handleItemOverview = () => {
    const from = (options.page - 1) * options.page_size + 1;
    const to = from + options.page_size - 1;
    const total = options.total;

    return `${from} - ${to > total ? total : to} / ${total} 件選択中`;
  };

  return ReactDOM.createPortal(
    <div id="modal" className="fixed inset-0 flex-middle-center bg-gray-800 bg-opacity-50 z-50">
      <div onClick={clickBackdrop} className="fixed inset-0 backdrop" />

      <div ref={modalRef} className="bg-white rounded-sm shadow-lg relative w-250 modal-content">
        <div className="flex-between-center px-6 py-4 ">
          <p className="font-bold">
            {openDownload.type === ENUM_TYPE_DOWNLOAD.CSV ? TEXT_DOWNLOAD.CSV_DOWNLOAD_CONFIRMATION : TEXT_DOWNLOAD.EXCEl_DOWNLOAD_CONFIRMATION}
          </p>
        </div>
        <div className="p-3 border-t border-white-720">
          <div className="max-h-138 w-full overflow-hidden grid grid-cols-1 gap-y-4">
            <p className="text-sm font-medium text-secondary-354">{handleItemOverview()}</p>
            <Table
              data={listSurveys}
              setSort={onSortChange}
              sort={sort}
              page={options.page}
              onPageChange={onPageChange}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
            />
          </div>
          {options.total > 0 && <ReportPagination classNameWrapper="!mt-2" options={options} onPageChange={onPageChange} loading={loadingData} />}
        </div>
        <div className="h-16 border-t border-white-720 flex-end-center gap-2 py-4 px-6">
          <Button className="h-8 text-sm w-25.5" text={TEXT_COMMON.CANCEL_TEXT} color="outline" onClick={closeModal} />
          {selectedRows.length > 0 ? (
            <Button className="h-8 text-sm w-34" text={TEXT_DOWNLOAD.DOWNLOAD} color="primary" onClick={onConfirm} />
          ) : (
            <Button className="h-8 text-sm w-34 border-gray-651 bg-white-795 !text-black/25" color="outline" text={TEXT_DOWNLOAD.DOWNLOAD} />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default React.memo(Download);
